import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { AlertCircle, Pencil, Plus, RefreshCw, Trash2 } from 'lucide-react';
import type { LearnedRuleRow, RuleScope, RuleStatus, UpsertRuleRequest } from '@command-room/shared';
import { RULE_SCOPES, RULE_STATUSES } from '@command-room/shared';
import { useRules, useUpsertRule, useDeleteRule, useReembedRule, useBulkDeleteRules } from '@/api/queries';
import { TableSkeleton } from '@/components/feedback/Skeletons';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectItem } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogClose } from '@/components/ui/dialog';
import { relativeTime } from '@/lib/format';

type RuleFormData = UpsertRuleRequest;

export function RuleDialog({
  initial,
  open,
  onOpenChange,
  lockedScope,
  lockedTarget,
}: {
  initial?: LearnedRuleRow;
  open: boolean;
  onOpenChange: (v: boolean) => void;
  lockedScope?: RuleScope;
  lockedTarget?: string;
}) {
  const upsert = useUpsertRule();
  const isLocked = !!lockedScope;

  const { register, handleSubmit, watch, setValue, reset, formState: { errors } } = useForm<RuleFormData>({
    defaultValues: {
      rule_text: initial?.rule_text ?? '',
      scope: initial?.scope ?? lockedScope ?? 'global',
      target_id: initial?.target_id ?? lockedTarget ?? '',
    },
  });

  const scope = watch('scope');
  const needsTarget = scope === 'department' || scope === 'agent' || scope === 'user';

  function onClose(open: boolean) {
    if (!open) reset();
    onOpenChange(open);
  }

  async function onSubmit(data: RuleFormData) {
    await upsert.mutateAsync({
      id: initial?.id,
      rule_text: data.rule_text,
      scope: data.scope,
      target_id: needsTarget ? (data.target_id || null) : null,
    });
    onClose(false);
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent
        title={initial ? 'Edit rule' : 'New rule'}
        description="Changes to rule text trigger a server-side re-embed so the rule is retrievable at runtime."
      >
        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="rule_text">Rule text</Label>
            <Textarea
              id="rule_text"
              {...register('rule_text', { required: true, minLength: 3 })}
              rows={5}
              placeholder="Describe the rule or behaviour pattern to remember…"
              className={errors.rule_text ? 'border-destructive' : ''}
            />
          </div>

          {!isLocked && (
            <div className="grid grid-cols-2 gap-4">
              <div className="flex flex-col gap-1.5">
                <Label>Scope</Label>
                <Select
                  value={scope}
                  onValueChange={(v) => setValue('scope', v as RuleScope)}
                >
                  {RULE_SCOPES.map((s) => (
                    <SelectItem key={s} value={s}>
                      {s.charAt(0).toUpperCase() + s.slice(1)}
                    </SelectItem>
                  ))}
                </Select>
              </div>
              {needsTarget && (
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="target_id">
                    Target{' '}
                    <span className="text-muted-foreground font-normal text-xs">
                      ({scope === 'agent' ? 'agent name' : scope === 'user' ? 'slack_user_id' : 'department'})
                    </span>
                  </Label>
                  <Input
                    id="target_id"
                    {...register('target_id')}
                    placeholder={scope === 'agent' ? 'Aaron' : scope === 'user' ? 'U0123456' : 'Engineering'}
                  />
                </div>
              )}
            </div>
          )}

          {upsert.error && (
            <p className="text-sm text-destructive flex items-center gap-1">
              <AlertCircle className="h-4 w-4" />
              {(upsert.error as Error).message}
            </p>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <DialogClose asChild>
              <Button type="button" variant="outline">Cancel</Button>
            </DialogClose>
            <Button type="submit" disabled={upsert.isPending}>
              {upsert.isPending ? 'Saving…' : 'Save rule'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function BulkDeleteDialog({
  confirm,
  onCancel,
  onConfirm,
  isPending,
}: {
  confirm: { ids: number[]; label: string } | null;
  onCancel: () => void;
  onConfirm: () => void;
  isPending: boolean;
}) {
  return (
    <Dialog open={!!confirm} onOpenChange={(open) => { if (!open) onCancel(); }}>
      <DialogContent
        title="Delete rules"
        description={`Delete ${confirm?.label ?? ''}? This cannot be undone.`}
      >
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onCancel}>Cancel</Button>
          <Button variant="destructive" disabled={isPending} onClick={onConfirm}>
            {isPending
              ? 'Deleting…'
              : `Delete ${confirm?.ids.length ?? 0} rule${confirm?.ids.length === 1 ? '' : 's'}`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function SelectAllCheckbox({
  checked,
  indeterminate,
  onChange,
}: {
  checked: boolean;
  indeterminate: boolean;
  onChange: (checked: boolean) => void;
}) {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = indeterminate;
  }, [indeterminate]);
  return (
    <input
      ref={ref}
      type="checkbox"
      checked={checked}
      onChange={(e) => onChange(e.target.checked)}
      className="h-4 w-4 cursor-pointer rounded border-border accent-primary"
    />
  );
}

function RuleRow({
  rule,
  onEdit,
  checked,
  onToggle,
}: {
  rule: LearnedRuleRow;
  onEdit: (r: LearnedRuleRow) => void;
  checked: boolean;
  onToggle: (id: number) => void;
}) {
  const deleteRule = useDeleteRule();
  const reembed = useReembedRule();
  const [confirmDelete, setConfirmDelete] = useState(false);

  return (
    <tr className="hover:bg-muted/20">
      <td className="px-3 py-3">
        <input
          type="checkbox"
          checked={checked}
          onChange={() => onToggle(rule.id)}
          className="h-4 w-4 cursor-pointer rounded border-border accent-primary"
        />
      </td>
      <td className="px-4 py-3 max-w-sm">
        <p className="line-clamp-3 text-sm">{rule.rule_text}</p>
      </td>
      <td className="px-4 py-3">
        <ScopeBadge scope={rule.scope} targetId={rule.target_id} targetDisplayName={rule.target_display_name} />
      </td>
      <td className="px-4 py-3">
        <Badge variant={rule.source === 'sweeper' ? 'secondary' : 'outline'}>
          {rule.source}
        </Badge>
      </td>
      <td className="px-4 py-3">
        <StatusBadge status={rule.status} absorbedInto={rule.absorbed_into} />
      </td>
      <td className="px-4 py-3">
        {rule.embedding_dirty ? (
          <Badge variant="warning">dirty</Badge>
        ) : (
          <Badge variant="success">ok</Badge>
        )}
      </td>
      <td className="px-4 py-3 text-xs text-muted-foreground whitespace-nowrap">
        {relativeTime(rule.created_at)}
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center justify-end gap-1">
          {rule.embedding_dirty && (
            <Button
              variant="ghost"
              size="icon"
              title="Re-embed"
              disabled={reembed.isPending}
              onClick={() => reembed.mutate(rule.id)}
            >
              <RefreshCw className="h-4 w-4" />
            </Button>
          )}
          <Button variant="ghost" size="icon" title="Edit" onClick={() => onEdit(rule)}>
            <Pencil className="h-4 w-4" />
          </Button>
          {confirmDelete ? (
            <div className="flex items-center gap-1">
              <Button
                variant="destructive"
                size="sm"
                disabled={deleteRule.isPending}
                onClick={() =>
                  deleteRule.mutate(rule.id, { onSuccess: () => setConfirmDelete(false) })
                }
              >
                Delete
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setConfirmDelete(false)}>
                Cancel
              </Button>
            </div>
          ) : (
            <Button
              variant="ghost"
              size="icon"
              title="Delete"
              onClick={() => setConfirmDelete(true)}
            >
              <Trash2 className="h-4 w-4 text-destructive" />
            </Button>
          )}
        </div>
      </td>
    </tr>
  );
}

function ScopeBadge({
  scope,
  targetId,
  targetDisplayName,
}: {
  scope: RuleScope;
  targetId: string | null;
  targetDisplayName?: string | null;
}) {
  const variants: Record<RuleScope, 'default' | 'secondary' | 'outline' | 'muted'> = {
    global: 'default',
    department: 'secondary',
    agent: 'outline',
    user: 'muted',
  };
  const label = targetDisplayName ?? targetId;
  return (
    <div className="flex flex-col gap-0.5">
      <Badge variant={variants[scope]}>{scope}</Badge>
      {label && (
        <span className="text-xs text-muted-foreground font-mono">{label}</span>
      )}
    </div>
  );
}

function StatusBadge({ status, absorbedInto }: { status: RuleStatus; absorbedInto: string | null }) {
  if (status === 'active') return <Badge variant="success">active</Badge>;
  const variant = status === 'archived' ? 'muted' : 'secondary';
  const title =
    status === 'absorbed' && absorbedInto
      ? `Condensed into ${absorbedInto} — content lives on there`
      : status === 'superseded'
        ? 'Replaced by a sharper rule with the same meaning'
        : 'No longer used';
  return (
    <div className="flex flex-col gap-0.5" title={title}>
      <Badge variant={variant}>{status}</Badge>
      <span className="text-[10px] text-muted-foreground">purges in 30d</span>
    </div>
  );
}

function RulesTable({
  rules,
  onEdit,
  selected,
  onToggle,
  onToggleAll,
  onRequestBulkDelete,
}: {
  rules: LearnedRuleRow[];
  onEdit: (r: LearnedRuleRow) => void;
  selected: Set<number>;
  onToggle: (id: number) => void;
  onToggleAll: (ids: number[], select: boolean) => void;
  onRequestBulkDelete: (ids: number[], label: string) => void;
}) {
  if (rules.length === 0) {
    return (
      <div className="py-12 text-center text-sm text-muted-foreground">
        No rules in this scope yet.
      </div>
    );
  }

  const allIds = rules.map((r) => r.id);
  const selectedInView = allIds.filter((id) => selected.has(id));
  const selectedCount = selectedInView.length;
  const allSelected = selectedCount === rules.length;
  const someSelected = selectedCount > 0 && !allSelected;

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between px-1">
        <div className="flex items-center gap-2.5">
          <SelectAllCheckbox
            checked={allSelected}
            indeterminate={someSelected}
            onChange={(checked) => onToggleAll(allIds, checked)}
          />
          <span className="text-xs text-muted-foreground">
            {selectedCount > 0
              ? `${selectedCount} of ${rules.length} selected`
              : `${rules.length} rule${rules.length === 1 ? '' : 's'}`}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="destructive"
            size="sm"
            disabled={selectedCount === 0}
            onClick={() =>
              onRequestBulkDelete(
                selectedInView,
                `${selectedCount} selected rule${selectedCount === 1 ? '' : 's'}`,
              )
            }
          >
            <Trash2 className="h-3.5 w-3.5" />
            Delete selected ({selectedCount})
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="text-destructive hover:text-destructive hover:bg-destructive/10"
            onClick={() =>
              onRequestBulkDelete(
                allIds,
                `all ${rules.length} rule${rules.length === 1 ? '' : 's'} in this view`,
              )
            }
          >
            Delete all ({rules.length})
          </Button>
        </div>
      </div>

      <div className="rounded-lg border border-border overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/30 text-left text-xs uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="px-3 py-3 w-8" />
              <th className="px-4 py-3">Rule</th>
              <th className="px-4 py-3">Scope</th>
              <th className="px-4 py-3">Source</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Embed</th>
              <th className="px-4 py-3">Added</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {rules.map((rule) => (
              <RuleRow
                key={rule.id}
                rule={rule}
                onEdit={onEdit}
                checked={selected.has(rule.id)}
                onToggle={onToggle}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function RulesPage() {
  const [statusFilter, setStatusFilter] = useState<RuleStatus | 'all'>('active');
  const { data: rules, isLoading } = useRules(undefined, undefined, statusFilter);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<LearnedRuleRow | undefined>(undefined);
  const [activeTab, setActiveTab] = useState('all');
  const [selected, setSelected] = useState(new Set<number>());
  const [bulkConfirm, setBulkConfirm] = useState<{ ids: number[]; label: string } | null>(null);
  const bulkDelete = useBulkDeleteRules();

  const dirtyCount = (rules ?? []).filter((r) => r.embedding_dirty).length;

  function openCreate() {
    setEditing(undefined);
    setDialogOpen(true);
  }

  function openEdit(rule: LearnedRuleRow) {
    setEditing(rule);
    setDialogOpen(true);
  }

  function handleTabChange(tab: string) {
    setActiveTab(tab);
    setSelected(new Set());
  }

  function toggle(id: number) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  function toggleAll(ids: number[], select: boolean) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (select) ids.forEach((id) => next.add(id));
      else ids.forEach((id) => next.delete(id));
      return next;
    });
  }

  function requestBulkDelete(ids: number[], label: string) {
    setBulkConfirm({ ids, label });
  }

  async function executeBulkDelete() {
    if (!bulkConfirm) return;
    await bulkDelete.mutateAsync(bulkConfirm.ids);
    setSelected(new Set());
    setBulkConfirm(null);
  }

  const byScope = (scope: RuleScope) => (rules ?? []).filter((r) => r.scope === scope);

  const tableProps = { onEdit: openEdit, selected, onToggle: toggle, onToggleAll: toggleAll, onRequestBulkDelete: requestBulkDelete };

  return (
    <div className="mx-auto flex max-w-7xl flex-col gap-6">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Learned rules</h1>
          <p className="text-sm text-muted-foreground">
            {rules?.length ?? 0} rule{rules?.length === 1 ? '' : 's'}
            {dirtyCount > 0 && (
              <span className="ml-2 text-warning-foreground">
                · {dirtyCount} embedding{dirtyCount === 1 ? '' : 's'} need re-embed
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select
            value={statusFilter}
            onValueChange={(v) => setStatusFilter(v as RuleStatus | 'all')}
            className="w-40"
          >
            <SelectItem value="active">Active</SelectItem>
            {RULE_STATUSES.filter((s) => s !== 'active').map((s) => (
              <SelectItem key={s} value={s}>
                {s.charAt(0).toUpperCase() + s.slice(1)}
              </SelectItem>
            ))}
            <SelectItem value="all">All statuses</SelectItem>
          </Select>
          <Button onClick={openCreate}>
            <Plus className="h-4 w-4" />
            New rule
          </Button>
        </div>
      </header>

      {isLoading ? (
        <TableSkeleton widths={['w-64', 'w-20', 'w-20', 'w-20', 'w-24', 'w-20']} rows={8} />
      ) : (
        <Tabs value={activeTab} onValueChange={handleTabChange}>
          <TabsList>
            <TabsTrigger value="all">All ({rules?.length ?? 0})</TabsTrigger>
            <TabsTrigger value="global">Global ({byScope('global').length})</TabsTrigger>
            <TabsTrigger value="department">Department ({byScope('department').length})</TabsTrigger>
            <TabsTrigger value="agent">Agent ({byScope('agent').length})</TabsTrigger>
            <TabsTrigger value="user">User ({byScope('user').length})</TabsTrigger>
          </TabsList>

          <TabsContent value="all" className="mt-4">
            <RulesTable rules={rules ?? []} {...tableProps} />
          </TabsContent>
          {(['global', 'department', 'agent', 'user'] as RuleScope[]).map((scope) => (
            <TabsContent key={scope} value={scope} className="mt-4">
              <RulesTable rules={byScope(scope)} {...tableProps} />
            </TabsContent>
          ))}
        </Tabs>
      )}

      <RuleDialog
        key={editing?.id ?? 'new'}
        initial={editing}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
      />

      <BulkDeleteDialog
        confirm={bulkConfirm}
        onCancel={() => setBulkConfirm(null)}
        onConfirm={executeBulkDelete}
        isPending={bulkDelete.isPending}
      />
    </div>
  );
}
